import React, { useEffect, useRef } from "react";
import "./Pagination.css";

export default function Pagination({ currentPage, totalPages, onPageChange }) {
  const isFirstRender = useRef(true);

  // Instantly scroll to the very top ONLY when the user clicks a page change.
  // We use a small timeout to let React paint the new list of products first so the browser doesn't interrupt the scroll.
  useEffect(() => {
    if (totalPages <= 1) return; // No pagination visible, no need to scroll
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTo?.(0, 0);
      document.documentElement.scrollTo?.(0, 0);
    }, 20); // 20ms ensures the layout has fully shifted

    return () => clearTimeout(timer);
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages) {
      pages.push(i);
    } else if (i >= currentPage - 2 && i <= currentPage + 2) {
      pages.push(i);
    }
  }

  const uniquePages = [...new Set(pages)].sort((a, b) => a - b);

  return (
    <nav className="pagination" aria-label="Product pagination">
      <button
        type="button"
        className="paginationBtn"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label="Previous page"
      >
        ← Prev
      </button>

      <div className="paginationPages">
        {uniquePages.map((p, idx) => {
          const prev = uniquePages[idx - 1];
          const showLeftEllipsis = prev !== undefined && p - prev > 1;
          return (
            <React.Fragment key={p}>
              {showLeftEllipsis && <span className="paginationEllipsis">…</span>}
              <button
                type="button"
                className={`paginationBtn ${currentPage === p ? "active" : ""}`}
                onClick={() => onPageChange(p)}
                aria-label={`Page ${p}`}
                aria-current={currentPage === p ? "page" : undefined}
              >
                {p}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <button
        type="button"
        className="paginationBtn"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label="Next page"
      >
        Next →
      </button>
    </nav>
  );
}
